import _ from 'lodash'
import moment from 'moment'
import Utils from '@/utils/utils'
import { v4 as uuidv4 } from 'uuid'

import { TaskItemTypes, TaskItemStatusTypes, TaskLinkedItemTypes, TaskTypeInvoicingRuleTypes, InventoryStockTypes, ItemOperationalReportingCodes, ItemOperationalReportingCodeLookup, OrderPaymentTypes, OrderStatusTypes } from '@/store/types'

export default {
  methods: {
    /**
     * Generate a name for the task item based on linked items
     * If all same contacts, will use that contact name
     * Otherwise if all have same delivery tag, will use that instead
     * Orders must contain order -> contact -> name
     */
    generateDespatchTaskItemNameForLinkedItems(taskItem, linkedItemList, options = {}) {
      if(!linkedItemList || linkedItemList.length == 0)
        return taskItem.locationAddress

      var contactList = _.uniqBy(_.compact(linkedItemList.map(linkedItem => {
        if(linkedItem.order && linkedItem.order.contact)
          return linkedItem.order.contact
        return null
      })), 'id')
      var orderList = _.uniqBy(_.compact(linkedItemList.map(linkedItem => linkedItem.order)), 'id')

      // Set task item name to first contact by default
      var taskItemName = taskItem.locationAddress
      if(contactList.length > 0)
        taskItemName = contactList[0].name

      // If multiple contacts, display contact count
      let contactCount = contactList.length
      if(contactCount > 1) {
        taskItemName = `${contactCount} Contacts`

        // If all orders are with the same delivery tag, use that instead
        let deliveryTagList = _.uniq(_.compact(orderList.map(order => {

          if(order.deliveryTag && order.deliveryTag.length > 0) {
            if(order.deliveryTag[0].name == '')
              return null

            return order.deliveryTag[0].name
          }

          return null
        })))

        if(deliveryTagList.length == 1)
          taskItemName = deliveryTagList[0]
      }

      // Check for address name lookups
      if(_.isArray(options.addressLookupList) && taskItem.isLocationEnabled) {
        let matchingAddrLookup = options.addressLookupList.find(addrLookup => {
          return Utils.compareAddressString(addrLookup.value, taskItem.locationAddress)
        })

        if(matchingAddrLookup)
          taskItemName = matchingAddrLookup.description
      }

      return taskItemName
    },


    /**
     * Generate the details/instructions for the task item based on orders
     * Uses delivery instructions from orders. Otherwise empty string
     */
    generateDespatchTaskDetailsForLinkedItems(taskItem, linkedItemList) {
      var orderList = _.uniqBy(_.compact(linkedItemList.map(linkedItem => linkedItem.order)), 'id')

      var deliveryInstrList = _.uniq(_.compact(orderList.map(order => ((order.deliveryInstructions || '') + '').trim())))
      return deliveryInstrList.join(', ')
    },


    /**
     * Calculate the best icon for a task type
     */
    getTaskTypeIcon(taskType) {
      if(!taskType)
        return 'fa-truck'
      
      if(taskType.isLocationTrackingEnabled && taskType.isDespatchEnabled)
        return 'fa-truck'

      if(!taskType.isLocationTrackingEnabled && taskType.isDespatchEnabled)
        return 'fa-person-carry'

      return 'fa-calendar-day'
    },


    /**
     * Check if an order item / purchase order contains trackable units to load / unload
     * Items like services / non-track-tracking items do not have units to load / unload
     */
    isOrderItemTrackableType(orderItem) {
      // Ignore items that have a parent item (eg, sub-items in assemblies/templates)
      if(orderItem.parentOrderItemId != null)
        return false

      // Ignore services
      if(orderItem.stockType == InventoryStockTypes.glassService)
        return false

      // If a general item, only include it if flagged for task completable
      if(orderItem.stockType == InventoryStockTypes.general && !orderItem.stock.isTrackingQuantity)
        return false

      return true
    },


    /**
     * Check if an order item can be included into the runsheet for tasks
     * Items like services / non-task-completable items cannot be included
     */
    isOrderItemRunsheetProcessable(orderItem) {
      // Ignore items that have a parent item (eg, sub-items in assemblies/templates)
      if(orderItem.parentOrderItemId != null)
        return false

      // Ignore services
      if(orderItem.stockType == InventoryStockTypes.glassService)
        return false

      // If a general item, only include it if flagged for task completable
      if(orderItem.stockType == InventoryStockTypes.general && !orderItem.stock.isTaskCompletable)
        return false

      return true
    },


    /**
     * Build the task item list given a list of orders for order despatch based tasks
     * Supports merging with another existing task
     * 
     * Adds in additional properties used by UI task item list
     * 
     * Returns a promise resolved on completion
     */
    generateTaskItemList(task, taskType, accountLocationId, currentTaskItemList, orderIdList, ignoreOrderItemIdList, ignorePOItemIdList, panelIdList, stockTransferList, purchaseOrderPickupIdList, purchaseOrderDespatchIdList, mergeTaskId = null, addressLookupList) {
      return new Promise(async (resolve, reject) => {
        try {
          var taskItemType = TaskItemTypes.orderDespatch

          var orderList = []
          var transferPanelList = []
          var purchaseOrderList = []
          var accountLocationList = []
          var mergeDestTask = null

          // If task type does not despatch items, just use a general task item type
          if(taskType.invoiceItemRuleType == TaskTypeInvoicingRuleTypes.none)
            taskItemType = TaskItemTypes.general

          var requestList = []
          if(orderIdList.length > 0) {
            requestList.push(this.$apiService.tasksService.getTaskOrderList(orderIdList))
          }
          if(panelIdList.length > 0) {
            requestList.push(this.$apiService.productionService.getProductionPanelList({ filter: { id: { in: panelIdList.join(',') }}}))
          }
          if(purchaseOrderPickupIdList.length > 0 || purchaseOrderDespatchIdList.length > 0) {
            requestList.push(this.$apiService.tasksService.getTaskPurchaseOrderList(_.uniq(_.concat(purchaseOrderPickupIdList, purchaseOrderDespatchIdList))))
          }
          if(mergeTaskId) 
            requestList.push(this.$apiService.tasksService.getTask(mergeTaskId))
          requestList.push(this.$apiService.adminService.getAccountLocations())

          if(requestList.length > 0) {
            var results = await Promise.all(requestList)
            let curResult = 0
            if(orderIdList.length > 0)
              orderList = results[curResult++].data
            if(panelIdList.length > 0)
              transferPanelList = results[curResult++].data
            if(purchaseOrderPickupIdList.length > 0 || purchaseOrderDespatchIdList.length > 0)
              purchaseOrderList = results[curResult++].data
            if(mergeTaskId)
              mergeDestTask = results[curResult++].data
            accountLocationList = results[curResult++].data
          }

          // Keep a copy of task item linked items to restore editing details (eg, operational codes) for reporting
          var currentLinkedItemsCopy = _.cloneDeep(currentTaskItemList.map(taskItem => taskItem.taskItemLinkedItems)).flat()         
    
          // Clear all non-merged linked items
          // Keep list to retain task item order
          currentTaskItemList.forEach(taskItem => {
            taskItem.taskItemLinkedItems = taskItem.taskItemLinkedItems.filter(linkedItem => linkedItem.fromMergedTask)
          })

          if(mergeDestTask) {
            // Just copy over the merge task items first...
            mergeDestTask.taskItems.forEach(mergeTaskItem => {
              // Ignore invalid task items
              if(mergeTaskItem.status == TaskItemStatusTypes.completed || mergeTaskItem.status == TaskItemStatusTypes.cancelled)
                return

              let existingTaskItem = currentTaskItemList.find(taskItem => mergeTaskItem.id == taskItem.taskItemId && taskItem.fromMergedTask)
              if(!existingTaskItem) {

                let newMergeTaskItem = {
                  id: uuidv4(),
                  type: mergeTaskItem.type,
                  taskItemId: mergeTaskItem.id,
                  fromMergedTask: true,
                  name: mergeTaskItem.name,
                  details: mergeTaskItem.details,
                  key: ('' + mergeTaskItem.locationAddress).toLowerCase(),
                  isLocationEnabled: mergeTaskItem.isLocationEnabled,
                  locationAddress: mergeTaskItem.locationAddress,
                  accountLocationId: mergeTaskItem.accountLocationId,
                  estimatedProcessingTime: mergeTaskItem.estimatedProcessingTime,
                  totalLinkedRequiredQuantity: this.calculateLinkedItemListRequiredQuantity(mergeTaskItem.taskItemLinkedItems),
                  expanded: false,
                  warningList: [],
                  taskItemLinkedItems: _.cloneDeep(mergeTaskItem.taskItemLinkedItems || [])
                }

                // Flag merged linked items as from the merged task
                newMergeTaskItem.taskItemLinkedItems.forEach(linkedItem => linkedItem.fromMergedTask = true)

                currentTaskItemList.push(newMergeTaskItem)
              }
            })
          }

          // If not merging tasks, remove items with merge task flag set
          if(!mergeDestTask) {
            currentTaskItemList = currentTaskItemList.filter(taskItem => !taskItem.fromMergedTask)
          }

          var addOrderToRunsheet = (order, orderItemIdList = [], ignoreItemIdList = [], isLoadedAtLocation = false) => {
            let curGroup = null
            let groupKey = ''
            let locationAddress = Utils.buildAddressString(order.deliveryAddress1, order.deliveryAddress2, order.deliverySuburb, order.deliveryPostcode, order.deliveryState, '', ' ')

            curGroup = this._findMatchingTaskItemGroup(taskType, currentTaskItemList, taskItemType, locationAddress, mergeDestTask)

            // TODO: If no group found, attempt to find a task item with Order items already in there for this item
            // Used when details overridden (eg, address) for a task item

            if(taskType.isLocationTrackingEnabled)
              groupKey = Utils.cleanupAddressString(locationAddress)

            // Filter out items we can ignore            
            let orderItemList = order.orderItems.filter(orderItem => {
              // Only items with quantities left to despatch
              if(orderItem.despatchedQuantity >= orderItem.quantity)
                return false

              // Filter by specific items required / ignore lists
              if(!orderItemIdList.includes(orderItem.id))
                return false
              if(ignoreItemIdList.includes(orderItem.id))
                return false

              // Only items that can be included in runsheets
              return this.isOrderItemRunsheetProcessable(orderItem)
            })

            // If no despatchable order items available, add it as a stand alone order
            // Only if setting available
            let taskItemLinkedItems = curGroup ? curGroup.taskItemLinkedItems : [] 

            if(orderItemList.length == 0 && taskType.isOrderCompleteEnabled) {
              if(!taskItemLinkedItems.some(linkedItem => linkedItem.orderId == order.id)) {
                let operationalInfo = this.calculateOperationalInfo(task, currentLinkedItemsCopy, TaskLinkedItemTypes.order, order)

                taskItemLinkedItems.push({
                  id: uuidv4(),
                  type: TaskLinkedItemTypes.order,
                  orderId: order.id,
                  order: order,
                  quantity: 1,
                  operationalReportingCode: operationalInfo.code,
                  operationalReportingNote: operationalInfo.note
                })
              }
            } else if(taskType.isOrderItemDespatchEnabled) {

              // Add all valid order items
              orderItemList.forEach(orderItem => {
                // Convert order items into a format for displaying on UI
                orderItem.order = {
                  id: order.id,
                  contact: order.contact,
                  sequenceNumber: order.sequenceNumber,
                  deliveryTag: order.deliveryTag,
                  deliveryInstructions: order.deliveryInstructions
                }

                // Avoid duplicates
                if(!taskItemLinkedItems.some(linkedItem => linkedItem.orderItemId == orderItem.id)) {
                  let operationalInfo = this.calculateOperationalInfo(task, currentLinkedItemsCopy, TaskLinkedItemTypes.orderItem, order)

                  taskItemLinkedItems.push({
                    id: uuidv4(),
                    type: TaskLinkedItemTypes.orderItem,
                    isLoadedAtLocation: isLoadedAtLocation,
                    orderId: order.id,
                    orderItemId: orderItem.id,
                    stockId: orderItem.stockId,
                    glassLevelId: orderItem.glassLevelId,
                    quantity: orderItem.quantity - orderItem.despatchedQuantity,
                    orderItem: orderItem,
                    order: order,
                    operationalReportingCode: operationalInfo.code,
                    operationalReportingNote: operationalInfo.note
                  })
                }                    
              })
            }

            // Add to existing TaskItems or create a new one
            if(curGroup) {
              curGroup.taskItemLinkedItems = taskItemLinkedItems
              curGroup.totalLinkedRequiredQuantity = this.calculateLinkedItemListRequiredQuantity(curGroup.taskItemLinkedItems)

              if(order.isOnHold) {
                curGroup.warningList.push(`Order ${order.sequenceNumber} is on hold`)
                curGroup.warningList = _.uniq(curGroup.warningList)
              }

            } else {
              let warningList = []

              if(order.isOnHold)
                warningList.push(`Order ${order.sequenceNumber} is on hold`)

              currentTaskItemList.push({
                id: uuidv4(),
                type: taskItemType,
                name: taskType.name,
                details: '',
                key: groupKey,
                expanded: false,
                warningList: warningList,
                estimatedProcessingTime: taskType.defaultTaskTime,
                isLocationEnabled: taskType.isLocationTrackingEnabled,
                locationAddress: locationAddress,
                totalLinkedRequiredQuantity: this.calculateLinkedItemListRequiredQuantity(taskItemLinkedItems),
                taskItemLinkedItems: taskItemLinkedItems
              })
            }
          }


          // Build selected orders into runsheet
          orderList.forEach(order => {

            addOrderToRunsheet(order, order.orderItems.map(item => item.id), ignoreOrderItemIdList)
          })

          // Panels for Internal Transfers
          transferPanelList.forEach(productionPanel => {
            let destAccLocationId = null
            let linkedProductionStageId = null

            // If a despatch transfer, it needs to go to the panel->order->accountLocation
            if(productionPanel.hasDespatchTransfers && !productionPanel.currentProductionStage) {
              destAccLocationId = productionPanel.order.accountLocationId
            }
            
            // If a production transfer, it needs to go to the panel -> curStage -> accountLocation
            if(productionPanel.hasProductionTransfers && productionPanel.currentProductionStage && productionPanel.currentProductionStage.curStageTask) {
              destAccLocationId = productionPanel.currentProductionStage.curStageTask.accountLocationId

              // Get linked production stage
              // May not always be set (if transferring panel for despatch)
              if(productionPanel.currentProductionStage.curStageTask.isTransferRequired)
                linkedProductionStageId = productionPanel.currentProductionStage.id
            }

            // Ignore if at the supplier. Needs to be external transfer
            if(productionPanel.currentSupplierId)
              return

            if(!destAccLocationId)
              return

            // If destination is same as the task source, then the task becomes 'go to panel location & pick it up'
            let isCollection = false
            if(destAccLocationId == accountLocationId && productionPanel.inventoryBin && productionPanel.inventoryBin.accountLocationId != accountLocationId) {
              destAccLocationId = productionPanel.inventoryBin.accountLocationId
              isCollection = true
            }

            // Get account location info
            let accLoc = accountLocationList.find(location => location.id == destAccLocationId)
            if(!accLoc)
              return

            let curGroup = this._findMatchingTaskItemGroup(taskType, currentTaskItemList, TaskItemTypes.transferInternal, accLoc.formattedAddress, mergeDestTask)
            let taskItemLinkedItems = curGroup ? curGroup.taskItemLinkedItems : []

            // Avoid duplicates
            if(!taskItemLinkedItems.some(linkedItem => linkedItem.productionPanelId == productionPanel.id)) {
              let operationalInfo = this.calculateOperationalInfo(task, currentLinkedItemsCopy, TaskLinkedItemTypes.productionPanel, productionPanel.order)

              taskItemLinkedItems.push({
                id: uuidv4(),
                type: TaskLinkedItemTypes.productionPanel,
                productionPanel: productionPanel,
                productionPanelId: productionPanel.id,
                productionStageId: linkedProductionStageId,
                orderItemId: productionPanel.orderItemId,
                orderId: productionPanel.orderId,
                order: productionPanel.order,
                orderItem: productionPanel.orderItem,
                stockId: productionPanel.stockId,
                isLoadedAtLocation: isCollection,
                quantity: 1,
                operationalReportingCode: operationalInfo.code,
                operationalReportingNote: operationalInfo.note
              })
            }
            
            if(curGroup) {
              curGroup.taskItemLinkedItems = taskItemLinkedItems
              curGroup.totalLinkedRequiredQuantity = this.calculateLinkedItemListRequiredQuantity(curGroup.taskItemLinkedItems)

            } else {
              currentTaskItemList.push({
                id: uuidv4(),
                type: TaskItemTypes.transferInternal,
                name: isCollection ? `Collect from ${accLoc.name}` : `Transfer to ${accLoc.name}`,
                details: '',
                key: Utils.cleanupAddressString(accLoc.formattedAddress),
                expanded: false,
                warningList: [],
                estimatedProcessingTime: taskType.defaultTaskTime,
                isLocationEnabled: taskType.isLocationTrackingEnabled,
                locationAddress: accLoc.formattedAddress,
                accountLocationId: accLoc.id,
                totalLinkedRequiredQuantity: this.calculateLinkedItemListRequiredQuantity(taskItemLinkedItems),
                taskItemLinkedItems: taskItemLinkedItems
              })
            }
          })

          // Panels for External Transfers
          // Location -> Supplier for purchase order
          transferPanelList.forEach(productionPanel => {
            let destPurchaseOrder = null
            let srcSupplier = null
            let linkedProductionStageId = null

            // Production transfers only with a linked purchase order
            if(productionPanel.hasProductionTransfers && productionPanel.currentProductionStage && productionPanel.currentProductionStage.curStageTask) {
              destPurchaseOrder = productionPanel.currentProductionStage.curStageTask.purchaseOrder

              // Get linked production stage
              if(productionPanel.currentProductionStage.curStageTask.isTransferRequired)
                linkedProductionStageId = productionPanel.currentProductionStage.id
            }

            // Pickup from a supplier
            if(productionPanel.currentSupplier)
              srcSupplier = productionPanel.currentSupplier

            if(!destPurchaseOrder)// && !srcSupplier)
              return

            let poAddress = 'Not Set'
            if(destPurchaseOrder.contactAddress)
              poAddress = destPurchaseOrder.contactAddress.formattedAddress

            let curGroup = this._findMatchingTaskItemGroup(taskType, currentTaskItemList, TaskItemTypes.transferExternal, poAddress, mergeDestTask)
            let taskItemLinkedItems = curGroup ? curGroup.taskItemLinkedItems : []

            // Avoid duplicates
            if(!taskItemLinkedItems.some(linkedItem => linkedItem.productionPanelId == productionPanel.id)) {
              let operationalInfo = this.calculateOperationalInfo(task, currentLinkedItemsCopy, TaskLinkedItemTypes.productionPanel, productionPanel.order)

              taskItemLinkedItems.push({
                id: uuidv4(),
                type: TaskLinkedItemTypes.productionPanel,
                productionPanel: productionPanel,
                productionPanelId: productionPanel.id,
                productionStageId: linkedProductionStageId,
                purchaseOrder: destPurchaseOrder,
                purchaseOrderId: destPurchaseOrder.id,
                orderItemId: productionPanel.orderItemId,
                orderId: productionPanel.orderId,
                order: productionPanel.order,
                orderItem: productionPanel.orderItem,
                stockId: productionPanel.stockId,
                quantity: 1,
                operationalReportingCode: operationalInfo.code,
                operationalReportingNote: operationalInfo.note
              })
            }
            
            if(curGroup) {
              curGroup.taskItemLinkedItems = taskItemLinkedItems
              curGroup.totalLinkedRequiredQuantity = this.calculateLinkedItemListRequiredQuantity(curGroup.taskItemLinkedItems)

            } else {
              currentTaskItemList.push({
                id: uuidv4(),
                type: TaskItemTypes.transferExternal,
                name: `Transfer to ${destPurchaseOrder.contact.name}`,
                details: '',
                key: Utils.cleanupAddressString(poAddress),
                expanded: false,
                warningList: [],
                estimatedProcessingTime: taskType.defaultTaskTime,
                isLocationEnabled: taskType.isLocationTrackingEnabled,
                locationAddress: poAddress,
                totalLinkedRequiredQuantity: this.calculateLinkedItemListRequiredQuantity(taskItemLinkedItems),
                taskItemLinkedItems: taskItemLinkedItems
              })
            }
          })

          // Stock for internal transfers
          stockTransferList.forEach(stockTransferInfo => {
            // Get dest account location info
            let accLoc = accountLocationList.find(location => location.id == stockTransferInfo.accountLocationId)
            if(!accLoc)
              return

            let curGroup = this._findMatchingTaskItemGroup(taskType, currentTaskItemList, TaskItemTypes.transferInternal, accLoc.formattedAddress, mergeDestTask)
            let taskItemLinkedItems = curGroup ? curGroup.taskItemLinkedItems : []

            // Avoid duplicates
            let existingLinkedItem = taskItemLinkedItems.find(linkedItem => {
              return (
                linkedItem.type == stockTransferInfo.type &&
                linkedItem.stockId == stockTransferInfo.stockId &&
                linkedItem.glassLevelId == stockTransferInfo.glassLevelId &&
                linkedItem.isLoadedAtLocation == stockTransferInfo.isLoadedAtLocation
              )
            })
            if(!existingLinkedItem) {
              taskItemLinkedItems.push({
                id: uuidv4(),
                type: stockTransferInfo.type,
                stockId: stockTransferInfo.stockId,
                stock: stockTransferInfo.stock,
                glassLevelId: stockTransferInfo.glassLevelId,
                glassLevel: stockTransferInfo.glassLevel,
                quantity: stockTransferInfo.quantity,
                isLoadedAtLocation: stockTransferInfo.isLoadedAtLocation,
                operationalReportingCode: ItemOperationalReportingCodes.none,
                operationalReportingNote: ''
              })
            }
            
            if(curGroup) {
              curGroup.taskItemLinkedItems = taskItemLinkedItems
              curGroup.totalLinkedRequiredQuantity = this.calculateLinkedItemListRequiredQuantity(curGroup.taskItemLinkedItems)

            } else {
              currentTaskItemList.push({
                id: uuidv4(),
                type: TaskItemTypes.transferInternal,
                name: stockTransferInfo.isLoadedAtLocation ? `Collect from ${accLoc.name}` : `Transfer to ${accLoc.name}`,
                details: '',
                key: Utils.cleanupAddressString(accLoc.formattedAddress),
                expanded: false,
                warningList: [],
                estimatedProcessingTime: taskType.defaultTaskTime,
                isLocationEnabled: taskType.isLocationTrackingEnabled,
                locationAddress: accLoc.formattedAddress,
                accountLocationId: accLoc.id,
                totalLinkedRequiredQuantity: this.calculateLinkedItemListRequiredQuantity(taskItemLinkedItems),
                taskItemLinkedItems: taskItemLinkedItems
              })
            }
          })


          // Purchase Order Pickups
          // External Transfer to collect panels / stock from supplier
          purchaseOrderList.forEach(purchaseOrder => {
            // Contact address of the supplier MUST be set
            if(!purchaseOrder.contactAddress)
              return

            // Ignore order items we don't want
            // Only use root items too
            purchaseOrder.purchaseOrderItems = purchaseOrder.purchaseOrderItems.filter(purchaseOrderItem => !ignorePOItemIdList.includes(purchaseOrderItem.id) && !purchaseOrderItem.parentOrderItemId)

            // Skip purchase orders with no items to pickup
            if(purchaseOrder.purchaseOrderItems.length == 0)
              return

            let curGroup = null
            let groupKey = ''
            let locationAddress = Utils.buildAddressString(purchaseOrder.contactAddress.address1, purchaseOrder.contactAddress.address2, purchaseOrder.contactAddress.suburb, purchaseOrder.contactAddress.postcode, purchaseOrder.contactAddress.state, '', ' ')
            
            curGroup = this._findMatchingTaskItemGroup(taskType, currentTaskItemList, TaskItemTypes.transferPickup, locationAddress, mergeDestTask)
            if(taskType.isLocationTrackingEnabled)
              groupKey = Utils.cleanupAddressString(locationAddress)
            let taskItemLinkedItems = curGroup ? curGroup.taskItemLinkedItems : []

            purchaseOrder.purchaseOrderItems.forEach(poItem => {
              // Avoid duplicates
              if(!taskItemLinkedItems.some(linkedItem => linkedItem.purchaseOrderItemId == poItem.id)) {
                taskItemLinkedItems.push({
                  id: uuidv4(),
                  type: TaskLinkedItemTypes.purchaseOrderItem,
                  isLoadedAtLocation: true,
                  purchaseOrderId: purchaseOrder.id,
                  purchaseOrderItemId: poItem.id,
                  stockId: poItem.stockId,
                  quantity: poItem.quantity,
                  purchaseOrderItem: poItem,
                  purchaseOrder: purchaseOrder,
                  operationalReportingCode: ItemOperationalReportingCodes.none,
                  operationalReportingNote: ''
                })
              }                    
            })

            if(curGroup) {
              curGroup.taskItemLinkedItems = taskItemLinkedItems
              curGroup.totalLinkedRequiredQuantity = this.calculateLinkedItemListRequiredQuantity(curGroup.taskItemLinkedItems)

            } else {
              currentTaskItemList.push({
                id: uuidv4(),
                type: TaskItemTypes.transferPickup,
                name: `Pickup goods from ${purchaseOrder.contact.name}`,
                details: '',
                key: groupKey,
                expanded: false,
                warningList: [],
                estimatedProcessingTime: taskType.defaultTaskTime,
                isLocationEnabled: taskType.isLocationTrackingEnabled,
                locationAddress: locationAddress,
                totalLinkedRequiredQuantity: this.calculateLinkedItemListRequiredQuantity(taskItemLinkedItems),
                taskItemLinkedItems: taskItemLinkedItems
              })
            }


            // Despatch Purchase Order
            // Handles if picking up items & then optionally despatching them to site in the one task
            if(!purchaseOrderDespatchIdList.includes(purchaseOrder.id))
              return

            // Get the order info & add the despatch to the runsheet
            // Only include order items that are linked to the PO items being picked up
            // Also check if TaskType matches up too
            if(purchaseOrder.order && purchaseOrder.order.orderItems) {
              let linkedOrderItems = purchaseOrder.order.orderItems.filter(orderItem => purchaseOrder.purchaseOrderItems.some(poItem => poItem.originalOrderItemId == orderItem.id))

              // Ignore if not the same task type
              if(purchaseOrder.order.despatchTaskTypeId != taskType.id)
                return

              if(linkedOrderItems.length > 0) {
                addOrderToRunsheet(purchaseOrder.order, linkedOrderItems.map(item => item.id), ignoreOrderItemIdList, true)
              }
            }

          })


          // Cleanup task items with no linked items if they require it
          currentTaskItemList = currentTaskItemList.filter(taskItem => {
            if(taskItem.type == TaskItemTypes.orderDespatch) {
              return taskItem.taskItemLinkedItems.some(linkedItem => linkedItem.type == TaskLinkedItemTypes.orderItem || linkedItem.type == TaskLinkedItemTypes.order)
            }
            if(taskItem.type == TaskItemTypes.transferInternal || taskItem.type == TaskItemTypes.transferExternal) {
              return taskItem.taskItemLinkedItems.some(linkedItem => {
                return [TaskLinkedItemTypes.productionPanel, TaskLinkedItemTypes.general, TaskLinkedItemTypes.glassLevel].includes(linkedItem.type)
              })
            }
            if(taskItem.type == TaskItemTypes.transferPickup) {
              return taskItem.taskItemLinkedItems.some(linkedItem => linkedItem.type == TaskLinkedItemTypes.purchaseOrderItem)
            }
            return true
          })

          // Generate all name/details info
          currentTaskItemList.forEach(taskItem => {
            if(taskItem.type == TaskItemTypes.orderDespatch || taskItem.type == TaskItemTypes.transferInternal || taskItem.type == TaskItemTypes.transferExternal || taskItem.type == TaskItemTypes.transferPickup) {

              if(taskItem.type == TaskItemTypes.orderDespatch) {
                if(!taskItem.editingName)
                  taskItem.name = this.generateDespatchTaskItemNameForLinkedItems(taskItem, taskItem.taskItemLinkedItems, { addressLookupList: addressLookupList })

                if(!taskItem.editingDetails)
                  taskItem.details = this.generateDespatchTaskDetailsForLinkedItems(taskItem, taskItem.taskItemLinkedItems)
              }

              let unitCount = taskItem.taskItemLinkedItems.reduce((curValue, linkedItem) => curValue + linkedItem.quantity, 0)

              // Estimate processing time
              // Stored in minutes. Round up
              if(!taskItem.editingDuration)
                taskItem.estimatedProcessingTime = Math.ceil(unitCount * taskType.handlingTimePerItem)
            }
          })

          return resolve(currentTaskItemList)
        } catch(err) {
          return reject(err)
        }
      })
    },

    _findMatchingTaskItemGroup(taskType, currentTaskItemList, taskItemType, address, mergeDestTask = null) {
      let curGroup = null
      if(taskType.isLocationTrackingEnabled) {
        var locationAddress = Utils.cleanupAddressString(address)

        // Attempt to merge with merge task items first
        if(mergeDestTask) {
          curGroup = currentTaskItemList.find(taskItem => {
            if(!taskItem.fromMergedTask)
              return false
            if(taskItem.type != taskItemType)
              return false
            return taskItem.key == locationAddress || Utils.compareAddressString(taskItem.locationAddress, locationAddress)
          })
        }

        if(!curGroup) {
          curGroup = currentTaskItemList.find(taskItem => {
            if(taskItem.type != taskItemType)
              return false
            return taskItem.key == locationAddress || Utils.compareAddressString(taskItem.locationAddress, locationAddress)
          })
        }
      } else {
        // If not location based, just group all items into the 1st relevant task item
        curGroup = currentTaskItemList.find(taskItem => taskItem.type == taskItemType)
      }
      return curGroup
    },

    calculateLinkedItemListRequiredQuantity(linkedItems) {
      if(!linkedItems)
        return 0

      return linkedItems.reduce((curValue, linkedItem) => {
        if(linkedItem.type == TaskLinkedItemTypes.orderItem) {
          if(!this.isOrderItemTrackableType(linkedItem.orderItem))
            return curValue
        
          return curValue + (linkedItem.orderItem.quantity - linkedItem.orderItem.despatchedQuantity)
        }

        // Transfers
        if(linkedItem.type == TaskLinkedItemTypes.productionPanel) {
          return curValue + linkedItem.quantity
        }

        // Stock Transfers
        if(linkedItem.type == TaskLinkedItemTypes.general || linkedItem.type == TaskLinkedItemTypes.glassLevel) {
          return curValue + linkedItem.quantity
        }

        // PO Pickups
        if(linkedItem.type == TaskLinkedItemTypes.purchaseOrderItem) {
          if(!this.isOrderItemTrackableType(linkedItem.purchaseOrderItem))
            return curValue
        
          return curValue + linkedItem.purchaseOrderItem.quantity
        }
        
        return curValue
      }, 0)
    },

    taskItemTotalScannedInQty(taskItem, scannedItemList) {
      if(!scannedItemList)
        return 0

      let scannedTaskItem = scannedItemList.find(scanTaskItem => scanTaskItem.id == taskItem.id)
      if(scannedTaskItem) {
        return scannedTaskItem.taskItemScannedItems.reduce((curValue, scannedItem) => {
          // Don't include scanned in quantity if not a trackable order item
          let orderItem = scannedItem.orderItem
          if(orderItem && !this.isOrderItemTrackableType(orderItem))
            return curValue

          return curValue + scannedItem.scannedInQuantity
        }, 0)
      }

      return 0
    },
    taskItemTotalScannedOutQty(taskItem, scannedItemList) {
      if(!scannedItemList)
        return 0

      let scannedTaskItem = scannedItemList.find(scanTaskItem => scanTaskItem.id == taskItem.id)
      if(scannedTaskItem) {
        return scannedTaskItem.taskItemScannedItems.reduce((curValue, scannedItem) => {
          // Don't include scanned out quantity if not a trackable order item
          let orderItem = scannedItem.orderItem
          if(orderItem && !this.isOrderItemTrackableType(orderItem))
            return curValue

          return curValue + scannedItem.scannedOutQuantity
        }, 0)
      }

      return 0
    },
    taskItemTotalRequiredQty(taskItem) {
      if(!taskItem || !taskItem.taskItemLinkedItems)
        return 0
      
      // Note: This will be zero after the task item is successfully completed & break the display of the loading info
      // So we store 'totalLinkedRequiredQuantity' value of task item as well & get largest
      // Done during migration to new task system where old task items didn't have the value set
      var requiredQuantity = taskItem.taskItemLinkedItems.reduce((curValue, linkedItem) => {
        if(linkedItem.type == TaskLinkedItemTypes.orderItem) {
          if(!this.isOrderItemTrackableType(linkedItem.orderItem))
            return curValue
        
          return curValue + (linkedItem.orderItem.quantity - linkedItem.orderItem.despatchedQuantity)
        }

        if(linkedItem.type == TaskLinkedItemTypes.productionPanel)
          return curValue + 1

        if(linkedItem.type == TaskLinkedItemTypes.general || linkedItem.type == TaskLinkedItemTypes.glassLevel)
          return curValue + linkedItem.quantity

        return curValue
      }, 0)

      return Math.max(taskItem.totalLinkedRequiredQuantity || 0, requiredQuantity)
    },
    taskItemRequiresLoading(taskItem) {
      if(taskItem.type != TaskItemTypes.orderDespatch)
        return false

      // Go through order items. If any is a trackable type, then it can be loadable
      if(taskItem.taskItemLinkedItems) {
        return taskItem.taskItemLinkedItems.some(linkedItem => {
          if(linkedItem.type == TaskLinkedItemTypes.orderItem) {
            if(linkedItem.orderItem)
              return this.isOrderItemTrackableType(linkedItem.orderItem)
          }

          // Transfer panel
          if(linkedItem.type == TaskLinkedItemTypes.productionPanel)
            return true

          // Transfer stock
          if(linkedItem.type == TaskLinkedItemTypes.general || linkedItem.type == TaskLinkedItemTypes.glassLevel)
            return true

          return false
        })
      }

      return false
    },

    /**
     * Calculate operational reporting info for a linked item
     * If an existing linked item is being updated, pass in the existing linked item to retain any existing operational reporting info if not being updated
     * Otherwise, attempt to calculate codes based on order info (production late, requested earlier, etc etc)
     * Returns none if no specific code could be determined. Requires operator to update
     */
    calculateOperationalInfo(task, existingLinkedItemList, linkedType, order) {
      let existing = existingLinkedItemList.find(linkedItem => linkedItem.type == linkedType && linkedItem.orderId == order.id)
      if(existing && existing.operationalReportingCode != ItemOperationalReportingCodes.none) {
        return {
          code: existing.operationalReportingCode,
          note: existing.operationalReportingNote
        }

      } else {

        // Attempt to figure out if any specific codes can be applied based on order info (late, requested earlier, etc etc)
        let code = ItemOperationalReportingCodes.none
        let note = ''

        // Compare requested vs estimated dates
        const dateRequested = moment(order.dateRequested).local()
        const dateEstimated = moment(order.dateEstimated).local()
        const dateDue = moment(order.dateRequested || order.dateEstimated).local()
        const dateEstimatedProdCompleted = moment(order.dateEstimatedProductionCompleted || order.dateDespatchStarted).local()    // Note: Stock only does not have estimated production completed for older orders
        const dateProductionCompleted = moment(order.dateProductionCompleted).local()
        const dateScheduled = moment(task.scheduleStartDate).local()
        const datePaymentSettled = moment(order.datePaymentSettled).local()

        // Check if production is late
        if(dateProductionCompleted.isValid() && dateEstimatedProdCompleted.isValid()) {
          if(dateProductionCompleted.isAfter(dateEstimatedProdCompleted)) {
            code = ItemOperationalReportingCodes.productionNotReady
          }
        }

        // Check if customer requested earlier than standard
        if(dateRequested.isValid() && dateEstimated.isValid()) {
          if(dateRequested.isBefore(dateEstimated, 'day')) {
            // Requested earlier - check if met
            if(dateScheduled.isValid()) {
              if(dateScheduled.isBefore(dateRequested, 'day')) {
                code = ItemOperationalReportingCodes.requestedEarlyMetEarly
              } else if(dateScheduled.isSameOrBefore(dateEstimated, 'day')) {
                code = ItemOperationalReportingCodes.requestedEarlyMetOnTime
              }
            }
          } else {
            // Requested after estimate
            if(dateRequested.isValid() && dateScheduled.isValid()) {
              if(dateScheduled.isSameOrBefore(dateRequested, 'day')) {
                code = ItemOperationalReportingCodes.onTime
              }
            }
          }
        } else {
          // Not requested earlier or later. Just based on standard estimate vs actual
          if(dateDue.isValid() && dateScheduled.isValid()) {
            if(dateScheduled.isSameOrBefore(dateDue, 'day')) {
              code = ItemOperationalReportingCodes.onTime
            }
          }
        }

        // Check if late & order was COD
        // Only if production completed on time & COD payment was late
        if(order.orderPaymentType == OrderPaymentTypes.cod) {
          if(dateDue.isValid() && dateScheduled.isValid()) {
            if(dateScheduled.isAfter(dateDue, 'day')) {

              if(dateEstimatedProdCompleted.isValid() && dateProductionCompleted.isValid() && datePaymentSettled.isValid()) {
                if(dateProductionCompleted.isSameOrBefore(dateEstimatedProdCompleted, 'day') && datePaymentSettled.isAfter('dateDue', 'day')) {
                  code = ItemOperationalReportingCodes.customerLatePayment
                }
              }

              if(order.status == OrderStatusTypes.pendingCODPaid)
                code = ItemOperationalReportingCodes.customerLatePayment
            }
          }
        }

        return {
          code: code,
          note: note
        }
      }
    },


    // Calculate the available report codes based on order/task
    calculateAvailableReportingCodeList(task, order) {
      const dateRequested = moment(order.dateRequested).local()
      const dateEstimated = moment(order.dateEstimated).local()
      const dateScheduled = moment(task.scheduleStartDate).local()
      const dateDue = moment(order.dateRequested || order.dateEstimated).local()

      var availableList = []

      if(dateScheduled.isValid() && dateDue.isValid() && dateScheduled.isSameOrBefore(dateDue, 'day')) {
        availableList.push(ItemOperationalReportingCodes.onTime)
      }

      if(dateRequested.isValid() && dateEstimated.isValid()) {
        if(dateRequested.isBefore(dateEstimated, 'day')) {
          availableList.push(ItemOperationalReportingCodes.requestedEarlyMetEarly)
          availableList.push(ItemOperationalReportingCodes.requestedEarlyMetOnTime)
        }

        availableList.push(ItemOperationalReportingCodes.requestEarlierNotReady)
        availableList.push(ItemOperationalReportingCodes.customerRequestedDateChange)
      }

      // If late, show reasons
      if(dateScheduled.isValid() && dateDue.isValid() && dateScheduled.isAfter(dateDue, 'day')) {
        availableList.push(ItemOperationalReportingCodes.customerNotReady)
        availableList.push(ItemOperationalReportingCodes.customerRefused)
        availableList.push(ItemOperationalReportingCodes.customerLatePayment)
        availableList.push(ItemOperationalReportingCodes.productionNotReady)
        availableList.push(ItemOperationalReportingCodes.productionBreakage)
        availableList.push(ItemOperationalReportingCodes.stockNotAvailable)
        availableList.push(ItemOperationalReportingCodes.resourceOverCapacity)
        availableList.push(ItemOperationalReportingCodes.runOverCapacity)
        availableList.push(ItemOperationalReportingCodes.runOptimised)
        availableList.push(ItemOperationalReportingCodes.resourceIssue)
        availableList.push(ItemOperationalReportingCodes.despatchBreakage)
        availableList.push(ItemOperationalReportingCodes.externalIssue)
      }

      // If no codes available, just default to 'externalIssue'
      // Shouldn't happen. But here for safety
      if(availableList.length == 0) {
        availableList.push(ItemOperationalReportingCodes.externalIssue)
      }

      return _.uniq(availableList)
    },

    taskOpCodeCategoryLookup(code) {
      var lookupInfo = ItemOperationalReportingCodeLookup.find(c => c.value == code)
      if(!lookupInfo)
        return 'none'
      return lookupInfo.category
    },

    isTaskItemOperationCodeEnabled(taskType, taskItem) {
      if(!taskType.isOperationalReportingEnabled)
        return false

      // Only allow operational codes for order despatch task items currently
      if(taskItem.type != TaskItemTypes.orderDespatch)
        return false

      return true
    },

    isOperationalCodeDisplayed(taskType, taskItem, linkedItem) {
      if(!this.isTaskItemOperationCodeEnabled(taskType, taskItem))
        return false      

      // Only display operational code for 1st linked item for each order
      if(!linkedItem.orderId)
        return true

      // Only for order/orderItem despatch linked items
      if(linkedItem.type != TaskLinkedItemTypes.order && linkedItem.type != TaskLinkedItemTypes.orderItem)
        return false

      var linkedItemsForOrder = taskItem.taskItemLinkedItems.filter(li => li.orderId == linkedItem.orderId)
      if(linkedItemsForOrder.length == 0)
        return true

      var firstLinkedItemForOrder = linkedItemsForOrder[0]
      return firstLinkedItemForOrder.id == linkedItem.id
    },
  }
}